import {
  ImmervisionDataManager,
  FishEyeViewType,
  Point,
  MountingType,
} from "./ImmervisionDataManager";

class ImmervisionManager {
  importedModule: null | any;
  controlModule: null | any;
  singleView: null | any;
  ePtzView: null | any;
  perimeterView: null | any;
  quadView: null | any;
  immervisionDataManager: ImmervisionDataManager;

  constructor(mounting: MountingType = "WALL") {
    this.importedModule = null;
    this.controlModule = null;
    this.singleView = null;
    this.ePtzView = null;
    this.perimeterView = null;
    this.quadView = null;
    this.immervisionDataManager = new ImmervisionDataManager(mounting);
  }

  async init() {
    try {
      this.importedModule = await import(
        /* webpackIgnore: true */
        "/immervision/jsm/immervision-sdk.js"
      );
      this.controlModule = await import(
        /* webpackIgnore: true */
        "/immervision/jsm/lib/control.js"
      );
    } catch (error) {
      console.error("Error loading Immervision modules:", error);
    }
  }

  updateMounting(orientation: MountingType): void {
    this.immervisionDataManager.updateMounting(orientation);
    this?.singleView?.iveViewer?.setState({ orientation });
    this?.ePtzView?.iveViewer?.setState({ orientation });
    this?.perimeterView?.iveViewer?.setState({ orientation });
    this?.quadView?.iveViewer?.setState({ orientation });
    this?.movePerimeter2();
  }

  private movePerimeter2(x = 0, y = 0) {
    if (
      !this.perimeterView?.iveViewer ||
      !this.immervisionDataManager ||
      this.immervisionDataManager.getMounting() !== "CEILING"
    ) {
      return;
    }
    const point = this.updateRotation(this.perimeterView.iveViewer.view, "perimeter", 0, x, y);
    const offset = this.immervisionDataManager.getOffset();
    const perimeterView2 = this.perimeterView.iveViewer.views[1];
    const point2 = perimeterView2.setRotation(point.x, point.y - offset);
    this.immervisionDataManager.updateRotation("perimeter", 1, point2);
  }

  loadSingleView(videoElement: any, outputElement: any) {
    if (this.singleView !== null) {
      this.singleView.iveViewer.video_html = undefined;
      this.singleView.iveViewer.destroy();
    }
    const IVEViewer = this.importedModule.IVEViewer;
    const Controller = this.controlModule.Control;
    const iveViewerSingle = new IVEViewer(
      {
        target: outputElement,
      },
      {
        video: videoElement,
        RPL: this.immervisionDataManager.getRpl(),
        orientation: this.immervisionDataManager.getMounting(),
      }
    );
    const view = iveViewerSingle.view;
    this.loadViewPosition(view, "single", 0);
    const controllerSingle = new Controller(outputElement, {
      onRotate: null,
      onZoom: null,
    });
    this.singleView = { iveViewer: iveViewerSingle, controller: controllerSingle };
    return this.singleView;
  }

  loadEPtzView(videoElement: any, outputElement: any) {
    if (this.ePtzView !== null) {
      this.ePtzView.iveViewer.video_html = undefined;
      this.ePtzView.iveViewer.destroy();
    }
    const IVEViewer = this.importedModule.IVEViewer;
    const Controller = this.controlModule.Control;
    const iveViewerSingle = new IVEViewer(
      {
        target: outputElement,
      },
      {
        video: videoElement,
        RPL: this.immervisionDataManager.getRpl(),
        orientation: this.immervisionDataManager.getMounting(),
      }
    );
    const view = iveViewerSingle.view;
    this.loadViewPosition(view, "ePtz", 0);
    const controllerSingle = new Controller(outputElement, {
      onZoom: (zoom: any) => this.updateZoom(view, "ePtz", 0, zoom),
      onRotate: (x: number, y: number) => this.updateRotation(view, "ePtz", 0, x, y),
    });
    this.ePtzView = { iveViewer: iveViewerSingle, controller: controllerSingle };
    return this.ePtzView;
  }

  loadPerimeterView(videoElement: any, outputElement1: any, outputElement2: any) {
    this.immervisionDataManager.getMounting() === "CEILING"
      ? this.loadPerimeterViewCeiling(videoElement, outputElement1, outputElement2)
      : this.loadPerimeterViewWall(videoElement, outputElement1);
  }

  private loadPerimeterViewCeiling(videoElement: any, outputElement1: any, outputElement2: any) {
    if (this.perimeterView !== null) {
      this.perimeterView.iveViewer.video_html = undefined;
      this.perimeterView.iveViewer.destroy();
    }
    const IVEViewer = this.importedModule.IVEViewer;
    const PerimeterView = this.importedModule.PerimeterView;
    const Controller = this.controlModule.Control;
    const iveViewerPerimeter = new IVEViewer(
      {
        target: outputElement1,
        view: "perimeter",
      },
      {
        video: videoElement,
        RPL: this.immervisionDataManager.getRpl(),
        orientation: this.immervisionDataManager.getMounting(),
      }
    );
    const perimeterView1 = iveViewerPerimeter.view;
    if (!outputElement2) {
      return;
    }
    const perimeterView2 = new PerimeterView(outputElement2, iveViewerPerimeter);
    const controllerPerimeter1 = new Controller(outputElement1, {
      onZoom: (zoom: any) => this.updateZoom(perimeterView1, "perimeter", 0, zoom),
      onRotate: (x: any, y: any) => {
        const point = this.updateRotation(perimeterView1, "perimeter", 0, x, y);
        const offset = this.immervisionDataManager.getOffset();
        const point2 = perimeterView2.setRotation(point.x, point.y - offset);
        this.immervisionDataManager.updateRotation("perimeter", 1, point2);
      },
    });
    const controllerPerimeter2 = new Controller(outputElement2, {
      onZoom: (zoom: any) => this.updateZoom(perimeterView2, "perimeter", 1, zoom),
      onRotate: (x: any, y: any) => {
        const point = this.updateRotation(perimeterView2, "perimeter", 1, x, y);
        const offset = this.immervisionDataManager.getOffset();
        const point2 = perimeterView1.setRotation(point.x, point.y + offset);
        this.immervisionDataManager.updateRotation("perimeter", 0, point2);
      },
    });
    this.perimeterView = {
      iveViewer: iveViewerPerimeter,
      controller: { controllerPerimeter1, controllerPerimeter2 },
    };
    const initPerimeter = () => this.initPerimeterPosition(perimeterView1);
    videoElement.removeEventListener("loadeddata", initPerimeter);
    videoElement.addEventListener("loadeddata", initPerimeter);
    initPerimeter();
    return this.perimeterView;
  }

  private loadPerimeterViewWall(videoElement: any, outputElement1: any) {
    if (this.perimeterView !== null) {
      this.perimeterView.iveViewer.video_html = undefined;
      this.perimeterView.iveViewer.destroy();
    }
    const IVEViewer = this.importedModule.IVEViewer;
    const PerimeterView = this.importedModule.PerimeterView;
    const Controller = this.controlModule.Control;
    const iveViewerPerimeter = new IVEViewer(
      {
        target: outputElement1,
        view: "perimeter",
      },
      {
        video: videoElement,
        RPL: this.immervisionDataManager.getRpl(),
        orientation: this.immervisionDataManager.getMounting(),
      }
    );
    const perimeterView1 = iveViewerPerimeter.view;
    const controllerPerimeter1 = new Controller(outputElement1, {
      onZoom: (zoom: any) => this.updateZoom(perimeterView1, "perimeter", 0, zoom),
      onRotate: (x: any, y: any) => {
        this.updateRotation(perimeterView1, "perimeter", 0, x, y);
      },
    });
    this.perimeterView = {
      iveViewer: iveViewerPerimeter,
      controller: { controllerPerimeter1 },
    };
    const initPerimeter = () => this.initPerimeterPosition(perimeterView1);
    videoElement.removeEventListener("loadeddata", initPerimeter);
    videoElement.addEventListener("loadeddata", initPerimeter);
    initPerimeter();
    return this.perimeterView;
  }

  loadQuadView(
    videoElement: any,
    outputElement1: any,
    outputElement2: any,
    outputElement3: any,
    outputElement4: any
  ) {
    if (this.quadView !== null) {
      this.quadView.iveViewer.video_html = undefined;
      this.quadView.iveViewer.destroy();
    }
    const IVEViewer = this.importedModule.IVEViewer;
    const PerspectiveView = this.importedModule.PerspectiveView;
    const Controller = this.controlModule.Control;
    const iveViewerQuad = new IVEViewer(
      {
        target: outputElement1,
      },
      {
        video: videoElement,
        RPL: this.immervisionDataManager.getRpl(),
        orientation: this.immervisionDataManager.getMounting(),
      }
    );
    const quadView1 = iveViewerQuad.view;
    const quadView2 = new PerspectiveView(outputElement2, iveViewerQuad);
    quadView2.initView = true;
    const quadView3 = new PerspectiveView(outputElement3, iveViewerQuad);
    quadView3.initView = true;
    const quadView4 = new PerspectiveView(outputElement4, iveViewerQuad);
    quadView4.initView = true;
    const ControllerQuad1 = new Controller(outputElement1, {
      onZoom: (zoom: any) => this.updateZoom(quadView1, "quad", 0, zoom),
      onRotate: (x: number, y: number) => this.updateRotation(quadView1, "quad", 0, x, y),
    });
    const ControllerQuad2 = new Controller(outputElement2, {
      onZoom: (zoom: any) => this.updateZoom(quadView2, "quad", 1, zoom),
      onRotate: (x: number, y: number) => this.updateRotation(quadView2, "quad", 1, x, y),
    });
    const ControllerQuad3 = new Controller(outputElement3, {
      onZoom: (zoom: any) => this.updateZoom(quadView3, "quad", 2, zoom),
      onRotate: (x: number, y: number) => this.updateRotation(quadView3, "quad", 2, x, y),
    });
    const ControllerQuad4 = new Controller(outputElement4, {
      onZoom: (zoom: any) => this.updateZoom(quadView4, "quad", 3, zoom),
      onRotate: (x: number, y: number) => this.updateRotation(quadView4, "quad", 3, x, y),
    });
    this.quadView = {
      iveViewer: iveViewerQuad,
      controller: { ControllerQuad1, ControllerQuad2, ControllerQuad3, ControllerQuad4 },
    };
    const initQuad = () => this.initQuadPosition(quadView1, quadView2, quadView3, quadView4);
    videoElement.removeEventListener("loadeddata", initQuad);
    videoElement.addEventListener("loadeddata", initQuad);
    initQuad();
    return this.quadView;
  }

  destroy() {
    if (this.singleView !== null && this.singleView.iveViewer !== null) {
      this.singleView.iveViewer.video_html = undefined;
      this.singleView.iveViewer.destroy();
      this.singleView.iveViewer = null;
      this.singleView.controller = null;
    }

    if (this.ePtzView !== null && this.ePtzView.iveViewer !== null) {
      this.ePtzView.iveViewer.video_html = undefined;
      this.ePtzView.iveViewer.destroy();
      this.ePtzView.iveViewer = null;
      this.ePtzView.controller = null;
    }

    if (this.perimeterView !== null && this.perimeterView.iveViewer !== null) {
      this.perimeterView.iveViewer.video_html = undefined;
      this.perimeterView.iveViewer.destroy();
      this.perimeterView.iveViewer = null;
      this.perimeterView.controller = null;
    }

    if (this.quadView !== null && this.quadView.iveViewer !== null) {
      this.quadView.iveViewer.video_html = undefined;
      this.quadView.iveViewer.destroy();
      this.quadView.iveViewer = null;
      this.quadView.controller = null;
    }
  }

  private initPerimeterPosition(perimeterView1: any) {
    this.loadViewPosition(perimeterView1, "perimeter", 0);
    this.movePerimeter2();
  }

  private initQuadPosition(quadView1: any, quadView2: any, quadView3: any, quadView4: any) {
    this.loadViewPosition(quadView1, "quad", 0);
    this.loadViewPosition(quadView2, "quad", 1);
    this.loadViewPosition(quadView3, "quad", 2);
    this.loadViewPosition(quadView4, "quad", 3);
  }

  private loadViewPosition(view: any, fishEyeViewType: FishEyeViewType, viewIndex: number): void {
    const rotationAbsolute = this.immervisionDataManager.getRotation(fishEyeViewType, viewIndex);
    view.setRotation(rotationAbsolute.x, rotationAbsolute.y);
    const zoom = this.immervisionDataManager.getZoom(fishEyeViewType, viewIndex);
    view.setZoom(zoom);
  }

  private updateRotation(
    view: any,
    fishEyeViewType: FishEyeViewType,
    viewIndex: number,
    x: number,
    y: number
  ): Point {
    const rot = view.rotate(x, y);
    const rotationPoint = { x: rot.x, y: rot.y };
    this.immervisionDataManager.updateRotation(fishEyeViewType, viewIndex, rotationPoint);
    return rotationPoint;
  }

  private updateZoom(
    view: any,
    fishEyeViewType: FishEyeViewType,
    viewIndex: number,
    zoomValue: number
  ): void {
    const maxZoom = 115;
    const zoomAbsolute = view.zoom(zoomValue);
    if (zoomAbsolute >= maxZoom) {
      view.setZoom(maxZoom);
    }
    const zoomToSave = zoomAbsolute > maxZoom ? maxZoom : zoomAbsolute;
    this.immervisionDataManager.updateZoom(fishEyeViewType, viewIndex, zoomToSave);
  }
}

export default ImmervisionManager;
